import { tabToName } from "@utils/utils";
import React, { useMemo } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";

import { Badge, Small, TabNavigation } from "@fronterahealth/frontera-ui-components";

import { CaregiverType, ProviderType, useCaregiverByIdQuery } from "@api/graphql/types-and-hooks";
import { PageHeader, PageHeaderTabsContainer } from "@components/PageHeader/PageHeader";
import { CaregiverAppStatus } from "@pages/Members/types";
import { buildBcbaNameByProviderId, mapCaregiverToMemberCaregiver } from "@pages/Members/utils/caregiverMapping";
import { useProvidersData } from "@providers/ProvidersDataProvider";

enum CaregiverDetailTab {
  Details = "details",
  Children = "children",
}

const caregiverDetailTabs = [CaregiverDetailTab.Details, CaregiverDetailTab.Children];

const appStatusBadge: Record<CaregiverAppStatus, { text: string; appearance: "neutral" | "warn" | "success" }> = {
  not_invited: { text: "Not invited", appearance: "neutral" },
  code_sent: { text: "Code sent", appearance: "warn" },
  connected: { text: "Connected", appearance: "success" },
};

export const CaregiverDetail: React.FC = () => {
  const { caregiverId } = useParams<{ caregiverId: string }>();
  const { pathname, state } = useLocation();

  const { providersQuery } = useProvidersData();

  const caregiverDetailsQuery = useCaregiverByIdQuery(
    { caregiverId: caregiverId || "" },
    {
      queryKey: ["caregiver-by-id", caregiverId],
      enabled: !!caregiverId,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const bcbaNameByProviderId = useMemo(
    () => buildBcbaNameByProviderId((providersQuery.data?.provider || []) as ProviderType[]),
    [providersQuery.data],
  );

  const caregiver = mapCaregiverToMemberCaregiver(
    caregiverDetailsQuery.data?.caregiverById as CaregiverType | undefined,
    bcbaNameByProviderId,
  );

  if (!caregiver) {
    if (caregiverDetailsQuery.isFetching) return null;
    return (
      <div className="w-full">
        <div className="mt-8 text-text-secondary">Caregiver not found.</div>
      </div>
    );
  }

  const statusBadge = appStatusBadge[caregiver.appStatus];

  return (
    <div className="flex h-full w-full flex-col">
      <PageHeader
        breadcrumbs={[
          { label: "User Management", href: "/members" },
          { label: "Caregivers", href: "/members?tab=caregivers" },
          { label: caregiver.name },
        ]}
        title={caregiver.name}
        meta={
          <>
            {caregiver.isPrimary && <Badge appearance="success" text="Primary" />}
            <Badge text={statusBadge.text} appearance={statusBadge.appearance} />
            <div className="flex items-center rounded-md border border-limestone-100 px-2 py-1">
              <Small colorType="secondary">
                {caregiver.email || "No email"}
                {caregiver.phone && <span> &middot; {caregiver.phone}</span>}
              </Small>
            </div>
          </>
        }
      />
      <PageHeaderTabsContainer>
        <TabNavigation
          tabs={caregiverDetailTabs.map((tab) => ({
            current: pathname.split("/")[pathname.split("/").length - 1] === tab,
            type: "component",
            name: tab,
            Component: (
              <Link to={tab} state={state}>
                {tabToName(tab)}
              </Link>
            ),
          }))}
        />
      </PageHeaderTabsContainer>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="py-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
